import { useQuery } from '@tanstack/react-query';

import {
  DtoV1AUSAusDocumentResponse,
  UWMCLibraryCommonDomainEnumsAusUnderwritingMethod,
} from '~/__generated__/api';
import { CREDIT } from '~/common/constants';
import useUrl from '~/common/hooks/useUrl';

import { typedApiClient } from '../util/api-client';

type UseGetAUSReportLinksParams = {
  requestId: string;
  ausMethod: number;
  requestDateTime: string;
  reportType: 'credit' | 'findings';
  requestStatus: boolean | null;
  enabled?: boolean;
};

export function useGetAUSReportLinks({
  requestId,
  ausMethod,
  requestDateTime,
  reportType,
  requestStatus,
  enabled = true,
}: UseGetAUSReportLinksParams) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: [
      'ausReportLinks',
      obfuscatedLoanIdentifier,
      requestId,
      ausMethod,
      requestDateTime,
      reportType,
      requestStatus,
    ],
    queryFn: async () => {
      const payload = {
        obfuscatedloanIdentifier: obfuscatedLoanIdentifier,
        requestIdentifier: {
          requestLdId: 1,
          requestId,
        },
        ausMethod:
          ausMethod as UWMCLibraryCommonDomainEnumsAusUnderwritingMethod,
        requestDateTime,
        requestStatus: reportType === 'credit' ? null : requestStatus,
      };

      let resp: { data: DtoV1AUSAusDocumentResponse };
      if (reportType === 'credit') {
        resp = await typedApiClient.v1.ausCreditReport(payload);
      } else {
        resp = await typedApiClient.v1.ausFindings(payload);
      }

      const parseData = (data: any) => {
        if (!data?.underwritingMethod || !data?.documentBase64)
          return undefined;

        if (data.underwritingMethod === CREDIT.DESKTOP_UNDERWRITER) {
          const dataRequestPath = atob(data.requestPath);
          return `${globalThis.globalConfig.edge.baseUrl}/${dataRequestPath}`;
        }

        if (data.underwritingMethod === CREDIT.LOAN_PRODUCT_ADVISOR) {
          const pdfUrl = atob(data.documentBase64);
          const prefixBaseMap: [string, string][] = [
            ['Lending/Origination/Loan', globalThis.globalConfig.ease.baseUrl],
            ['Underwriting/AUS', globalThis.globalConfig.edge.baseUrl],
          ];
          const match = prefixBaseMap.find(([prefix]) =>
            pdfUrl.startsWith(prefix),
          );
          return `${match?.[1] ?? globalThis.globalConfig.ease.baseUrl}/${pdfUrl}`;
        }

        return undefined;
      };

      return parseData(resp?.data);
    },
    enabled:
      enabled &&
      !!obfuscatedLoanIdentifier &&
      !!requestId &&
      !!ausMethod &&
      !!requestDateTime,
    refetchOnWindowFocus: false,
  });
}
